/**
 * SKIP TO CONTENT — the first thing in the tab order, and invisible until it
 * matters.
 *
 * Every screen in this product puts a rail, a header and (on the desk) a step
 * list before its content. Without this, reaching the actual page by keyboard
 * costs a dozen Tab presses on every navigation — which is the difference
 * between "usable by keyboard" and "technically reachable by keyboard".
 *
 * It is not hidden with `display: none` or `visibility: hidden`: both remove it
 * from the tab order, which would defeat the entire purpose. It is positioned
 * off-screen and comes back on focus, which is the one technique that keeps it
 * focusable while invisible.
 */
import React from 'react';
import styled from '@emotion/styled';
import {
  duration,
  easing,
  layout,
  palette,
  radius,
  shadow,
  spacing,
  typography,
  zIndex,
} from '../../style-utils/theme';

const Link = styled.a`
  position: absolute;
  left: ${spacing.sm};
  top: -100px;
  z-index: ${zIndex.overlay};
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${radius.xs};
  background: ${palette.surface};
  color: ${palette.lagoon};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  text-decoration: none;
  box-shadow: ${shadow.md};
  /* On the motion scale (duration.fast / easing.standard), and tall enough to
     be a real target: the link measured 167 x 37 on all 40 screens it appears
     on, against layout.touchTarget of 44. */
  min-height: ${layout.touchTarget};
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  transition: top ${duration.fast} ${easing.standard};

  &:focus {
    top: ${spacing.sm};
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

export const SkipLink: React.FC = () => (
  <Link href="#main-content">Skip to main content</Link>
);
